import React, { useState, useEffect, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ITableColumn } from "../../../../components";
import DataTable from "../../../../components/components/Table/DataTable";
import { CustomerPolicyDetailLabels as cstPolicyCoverageLabel } from '../../../../shared/util/label';
import { useConfirmationModalContext } from "../../../../shared/components/ModalConfirmationContext";
import { RootState } from "../../../../shared/store";
import { updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import { uuid } from "../../../../shared/util/uuid";
import { PolicyLOBReadDto } from "../../../model/PolicyLOBReadDto";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { PolicyLocationReadDto } from "../../../model/PolicyLocationReadDto";
import './CustomerEditPolicyFloodCoverageGrid.scss';
import { PolicyFloodCoverageDto } from "../../../model/PolicyFloodCoverageDto";
import CustomerFloodCoverageConfig from "./CustomerFloodCoverageConfig";
import './CustomerEditPolicyFloodCoverageGrid.scss';
import CustomerFloodCoverageForm from "./CustomerFloodCoverageForm";
import * as constant from '../../../../shared/util/constant';

export interface ICustomerEditPolicyFloodCoverageGrid {
  customerId?: string;
  policyId?: string;
  policyEventId?: string;
  validationErrors?: any;
  tabIndex?: number;
}

export interface FloodCoverageState {
  isEditing: boolean;
  isAddingNewCoverage: boolean;
}

const initialFloodCoverageState: FloodCoverageState = {
  isEditing: false,
  isAddingNewCoverage: false
};

const CustomerEditPolicyFloodCoverageGrid: React.FunctionComponent<ICustomerEditPolicyFloodCoverageGrid> = (props) => {
  let tbIndex = props.tabIndex;
  const { customerId, policyId, policyEventId, validationErrors } = props;
  const dispatch = useDispatch();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [isFormSaved, setIsFormSaved] = useState(true);
  const [currentFloodCoverageData, setCurrentFloodCoverageData] = useState<PolicyFloodCoverageDto | null>(null);
  const modalContext = useConfirmationModalContext();
  const [policyFloodCoverages, setPolicyFloodCoverages] = useState<PolicyFloodCoverageDto[]>([]);
  const [lobs, setLobs] = useState<PolicyLOBReadDto[]>([]);
  const customerDetail = useSelector((state: RootState) => state?.customerSlice);
  const policyDetailKey = CommonUtility.getPolicyDetailKey(policyId, policyEventId);
  const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];
  const selectedLocationDetails = customerDetail.selectedLocationDetails;
  const [floodCoverageState, setFloodCoverageState] = useState<FloodCoverageState>(initialFloodCoverageState);
  const customerFloodCoverageConfig = new CustomerFloodCoverageConfig();
  const [polLocations, setPolLocations] = useState<PolicyLocationReadDto[]>([]);

  const annualTotalSum = policyFloodCoverages.reduce((acc, row) => acc + (row.annualTotal || 0.00), 0.00);
  useEffect(() => {
      // Fetch coverage data from the customer slice
      const lobArray = policyDetails?.policyLineOfBusinesses || [];
      setLobs(lobArray);
      const policyLocations = lobArray.flatMap(lob => lob.policyLocations || [])
      .filter(lob => lob.polEventId === policyEventId);
      setPolLocations(policyLocations);

      // Extract coverages that have isCoverage=true
      const coverages = lobArray
        .flatMap(lob => lob.policyFloodCoverages || [])
      setPolicyFloodCoverages(coverages);

  }, [policyId, policyEventId, selectedLocationDetails]);

 const onRowClick = (rowData: any, column: ITableColumn) => {
    // Don't allow clicking while already editing a different row
    if (floodCoverageState.isEditing && expandedRowId !== rowData.id) {
      return;
    }

    // If clicking the same row that's expanded, collapse it
    if (expandedRowId === rowData.id) {
      setExpandedRowId(null);
      setCurrentFloodCoverageData(null);
      setPolicyFloodCoverages([...policyFloodCoverages]);
      setFloodCoverageState({ isEditing: false, isAddingNewCoverage: false });
    } else {
      // Otherwise, expand the clicked row
      setExpandedRowId(rowData.id);
      setCurrentFloodCoverageData(rowData);
      setFloodCoverageState({ isEditing: true, isAddingNewCoverage: false });
    }
  };

  const onCancelClick = async () => {    
    setExpandedRowId(null);
    setCurrentFloodCoverageData(null);
    setIsFormSaved(true);
    setFloodCoverageState({ isEditing: false, isAddingNewCoverage: false });
  };

const onRowDeleteClick = async (rowData: any, column: ITableColumn) => {
    try {

        const result = await modalContext.showConfirmation("Are you sure you want to delete this coverage?");
        if (!result) {
            return;
        }

        const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));
        
        if (updatedPolicyDetails?.policyLineOfBusinesses) {
            const updatedLOBs = updatedPolicyDetails.policyLineOfBusinesses.map(lob => {
                if (policyEventId && lob.polEventId !== policyEventId) {
                    return lob;
                }
                
                if (lob.policyFloodCoverages?.length > 0) {
                    return {
                        ...lob,
                        policyFloodCoverages: lob.policyFloodCoverages.filter(coverage => coverage.id !== rowData.id)
                    };
                }
                
                return lob;
            });
            
            updatedPolicyDetails.policyLineOfBusinesses = updatedLOBs;
            
            setPolicyFloodCoverages(prevCoverages => 
                prevCoverages.filter(coverage => coverage.id !== rowData.id)
            );
    
            dispatch(updateSelectedPolicyDetails({ 
                policyId: policyId, 
                polEventId: policyEventId, 
                policyDetails: updatedPolicyDetails 
            }));

            setExpandedRowId(null);
            setCurrentFloodCoverageData(null);
            setFloodCoverageState({ isEditing: false, isAddingNewCoverage: false });
            setIsFormSaved(true);
        }
    } catch (ex) {
        console.error("Error deleting coverage", ex);
        // Even on error, ensure UI state is reset
        setFloodCoverageState({ isEditing: false, isAddingNewCoverage: false });
        setIsFormSaved(true);
    }
};

  // Add handler for Add New Coverage click - similar to CustomerLoss
  const onAddNewCoverageClick = (e) => {
    if (floodCoverageState.isAddingNewCoverage || floodCoverageState.isEditing) {
      return;
    }
    const newCoverage: PolicyFloodCoverageDto = {
      id: uuid(),
      buildingLimit1: null,
      buildingPremium: null,
      contentLimit1: null,
      contentPremium: null,
      annualTotal: null,
      discountSurchargesTotal: null,
      additionalCoverageTotal : null,
      prePaidTotal: null,
      effDate: policyDetails?.polEffDate,
      lobId: lobs[0]?.id,
      polId: policyDetails?.id,
      polEventId: lobs[0]?.polEventId,
      statusTypeId: constant.StatusTypeIds.ADD,
    };

    // Add the new coverage to the list at the top for display
    setPolicyFloodCoverages([newCoverage, ...policyFloodCoverages]);
    setCurrentFloodCoverageData(newCoverage);
    setFloodCoverageState({ isEditing: false, isAddingNewCoverage: true });
    setExpandedRowId(newCoverage.id); // Set the expanded row to the new row's ID
  };

  const buttonLabel = cstPolicyCoverageLabel.ADD_CUSTOMER_COVERAGE;
  let AddNewCoverageButton = <></>;
  if (!floodCoverageState.isAddingNewCoverage && !floodCoverageState.isEditing) {
    AddNewCoverageButton = (
      <Button
        className="mb-2"
        onClick={(e) => onAddNewCoverageClick(e)}
        tabIndex={tbIndex++}
        id={"btnCoverageAdd"}
        disabled={polLocations.length === 0}
      >
        <i className="bi bi-plus-circle me-2"></i>
        {buttonLabel}
      </Button>
    );
  }

  // Coverage form component
  const floodCoverageFormView = (
    <CustomerFloodCoverageForm
      customerId={customerId}
      polEventId={policyEventId}
      polId={policyId}
      floodCoverageId={currentFloodCoverageData?.id}
      customerFloodCoverage={currentFloodCoverageData}
      setIsFormSaved={setIsFormSaved}
      formValidation={validationErrors}
      tabIndex={tbIndex}
    />
  );

  // Render the form row for the expanded row
  const renderRow = (coverage, index) => {
    return (
    <React.Fragment key={coverage.id || index}>
      {((floodCoverageState.isEditing || floodCoverageState.isAddingNewCoverage) && 
        currentFloodCoverageData && currentFloodCoverageData.id === coverage.id) && (
        <tr>
          <td colSpan={tableColumns.length}>
            <div className="customer-coverage-form-wrapper">
              {floodCoverageFormView}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
    );
  };

  // Get columns from config with row click handler and cancel handler
  const tableColumns: ITableColumn[] = customerFloodCoverageConfig.getFloodCoverageTableColumns(
    onRowClick, 
    onCancelClick, 
    onRowDeleteClick,
    tbIndex
  );

  return (
    <div className="customer-basic-coverage-table">
      <div className="coverage-actions">
        {AddNewCoverageButton}
      </div>

      <DataTable
        columns={tableColumns}
        data={policyFloodCoverages}
        keyColumnName="id"
        expandedRowId={expandedRowId}
        setExpandedRowId={setExpandedRowId}
        renderRow={renderRow}
        isFormSaved={isFormSaved}
        tableClassName="table-striped table-hover table-sm"
        theadClassName="tblHeader"
      />

      <div className="annualTotalSum">
        <span>Total:${annualTotalSum.toFixed(2)}</span>
      </div>
    </div>
  );
};

export default CustomerEditPolicyFloodCoverageGrid;